import { darken } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { Image } from '../../../concepts/resources/Image';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { Physical } from '../../canvas/entities/Physical';
import { Participant } from '../../canvas/participants/Participant';
import { useParticipant2 } from '../../canvas/participants/ParticipantHooks';
import { RenderBox, RenderPressable, usePressableContext } from '../../canvas/visualization/RenderHelpers';
import { TaskLayout } from './TaskLayout';

const useLayout = (object: Physical) => {
    if ('layout' in object) {
        const layout = object.layout as any;
        if ('task' in layout) {
            return layout.task as TaskLayout;
        }
    }
    return null;
};

const RenderDesign = ({ layout }: { layout: TaskLayout }) => {

    const state = usePressableContext();

    // background: #e5e7eb;
    // color: #546077;
    // font - weight: 500;
    // font - size: 0.8125rem;

    const color = layout.toggle.color;

    const fill =
        (state === 'idle' ? color : null) ??
        (state === 'hover' ? darken(0.1, color) : null) ??
        (state === 'active' ? darken(0.2, color) : null) ??
        'transparent';

    return <RenderBox
        {...layout.frame.toCSS()}
        background={fill}
        borderRadius="5px" />;
};

const RenderImage = styled.div<{ color: string }>`
    display: block;
    position: relative;
    border-radius: 3px;
    width: 100%;
    height: 100%;
    background: ${p => p.color};
    
    img, svg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    svg {
        transform: scale(0.8);
    }
`;

const RenderInitials = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 1px;
    width: 100%;
    height: 100%;
    border-radius: 3px;
    font-family: Rubik;
    font-size: 8px;
    font-weight: 450;
    color: white;
    background: ${p => p.color};
`;

const RenderUser = React.memo(({ userId, area }: { userId: string, area: Rect }) => {
    const user = useObserver(useParticipant2(userId));
    return <RenderBox {...area.inflate(-1).toCSS()} display="flex" alignItems="center" justifyContent="center" border="1px solid #fff" borderRadius="4px" overflow="hidden">
        {!!user?.photo && <RenderImage color={user.color}><Image src={user.photo} /></RenderImage>}
        {!!user && !user?.photo && <RenderInitials color={user.color}>{Participant.toInitials(user.name)}</RenderInitials>}
        {!user && <RenderInitials color={Colors.primaryLight}>?</RenderInitials>}
        {/* {state === 'active' && <RenderBox background="rgba(0,0,0,0.1)" width="100%" height="100%" borderRadius="8px" />} */}
    </RenderBox>;
});

const RenderToggle = React.memo(({ area, color, label }: { area: Rect, color: string, label: string, status: string }) => {

    // background: #e5e7eb;
    // color: #546077;
    // font - weight: 500;
    // font - size: 0.8125rem;

    return <RenderBox
        {...area.toCSS()}
        borderRadius="1px"
        display="flex"
        alignItems="center"
        justifyContent="start"
        gap="1px"
        color={Colors.contrastTextColorFor(color)}
        fontFamily="Rubik"
        fontSize="9px"
        fontWeight="450"
        lineHeight="1em"
        textTransform="uppercase"
    >
        <span>{label}</span>
        <Icon size="12px" src="deqo://icons/hero-mini/chevron-down" />
    </RenderBox>;
});

export const RenderTask = ({ object }: { object: Physical & { layout: any } }) => {

    const layout = useLayout(object);
    if (!layout) return null;

    return <>
        <RenderPressable object={object} hint="task">
            <RenderDesign layout={layout} />
            <RenderUser {...layout.user} />
            <RenderToggle {...layout.toggle} />
        </RenderPressable>
    </>;
};
